import { Link } from "react-router-dom";
import { ArrowRight, UserCheck, Users, Compass, FileCheck2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/motion";
import usePageMeta from "@/hooks/usePageMeta";
import { BRAND, IMAGES } from "@/data/content";

const SERVICES = [
  {
    icon: UserCheck,
    title: "1-on-1 Consulting",
    desc: "Private, focused sessions on your specific trade challenge — market entry, pricing, buyer negotiation, or a deal you're stuck on.",
  },
  {
    icon: Users,
    title: "Group Training & Workshops",
    desc: "Structured programs for teams and cohorts: export readiness, Incoterms, documentation, buyer development and more.",
  },
  {
    icon: Compass,
    title: "Ongoing Coaching",
    desc: "A mentor in your corner as you grow — regular check-ins, deal reviews and course-corrections through your first years of trade.",
  },
  {
    icon: FileCheck2,
    title: "Documentation & Compliance",
    desc: "Practical guidance on invoices, packing lists, shipping bills, LC terms and customs compliance — error-free, every time.",
  },
];

const PROCESS = [
  { step: "Discovery Call", desc: "A free, no-pressure conversation to understand where you are and where you want to go." },
  { step: "Assessment", desc: "An honest evaluation of your export readiness — product, pricing, documents and target markets." },
  { step: "Training / Coaching Plan", desc: "A tailored roadmap: what to learn, what to fix, and in what order — with clear milestones." },
  { step: "Ongoing Support", desc: "Regular reviews and on-call guidance as you ship your first orders and scale beyond them." },
];

export default function Consulting() {
  usePageMeta(
    `Export-Import Consulting, Training & Coaching — ${BRAND.name}`,
    "Export-import consulting, workshops and coaching for MSMEs and startups entering global trade. From first enquiry to first shipment."
  );

  return (
    <div data-testid="consulting-page">
      <PageHero
        kicker="Pillar 02 — Coaching"
        lines={["Consulting, Training", "& Coaching."]}
        description="For MSMEs and startups stepping into global trade. Learn from someone who has spent 40 years on the frontline — and genuinely wants to see you succeed."
        accent="gold"
        image={IMAGES.consulting}
      />

      {/* WHO IT'S FOR */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <Reveal>
              <p className="kicker mb-5 text-gold">Who It's For</p>
              <h2 className="font-serif text-3xl leading-tight text-white md:text-4xl">
                Built for MSMEs & Startups <span className="italic text-gold">Entering Global Trade.</span>
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-slate-400 md:text-base">
                You have a product worth exporting, but the world of buyers, banks, Incoterms and
                customs feels like a maze. I've spent four decades in that maze — and I know every
                shortcut, dead end and hidden door.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-slate-400 md:text-base">
                Whether you're a first-generation entrepreneur or a family business going global for
                the first time, the goal is the same: your first successful shipment, and the
                confidence to repeat it.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-2xl border border-[#1E3A5F]">
                <img
                  src={IMAGES.consulting}
                  alt="Mahesh Panchal conducting an export-import training session"
                  data-testid="consulting-training-photo"
                  className="aspect-[3/4] w-full object-cover object-top"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal className="mb-14 max-w-2xl">
            <p className="kicker mb-5 text-gold">What's Offered</p>
            <h2 className="font-serif text-3xl leading-tight text-white md:text-4xl">
              Four Ways I Can <span className="italic text-gold">Help You Win.</span>
            </h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1} className="h-full">
                <div className="h-full rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-8 transition-[transform,border-color] duration-500 hover:-translate-y-2 hover:border-gold/50">
                  <s.icon className="h-7 w-7 text-gold" strokeWidth={1.5} />
                  <h3 className="mt-6 font-serif text-xl text-white">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal className="mb-14 max-w-2xl">
            <p className="kicker mb-5 text-gold">How It Works</p>
            <h2 className="font-serif text-3xl leading-tight text-white md:text-4xl">
              A Simple, Proven <span className="italic text-gold">Four-Step Path.</span>
            </h2>
          </Reveal>
          <div className="border-t border-[#1E3A5F]">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.06}>
                <div className="grid items-baseline gap-3 border-b border-[#1E3A5F] py-9 md:grid-cols-12 md:gap-6">
                  <span className="font-serif text-4xl text-gold/35 md:col-span-2">0{i + 1}</span>
                  <h3 className="font-serif text-2xl text-white md:col-span-4">{p.step}</h3>
                  <p className="text-sm leading-relaxed text-slate-400 md:col-span-6 md:text-base">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-14 text-center">
            <Link to="/contact" data-testid="consulting-discovery-button" className="btn-primary">
              Book a Free Discovery Call <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTASection
        kicker="Start Here"
        title="Your first shipment is closer than you think."
        subtitle="Book a free discovery call — bring your questions, leave with a plan."
        ctaLabel="Book a Free Discovery Call"
      />
    </div>
  );
}
